import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";

/* -----------------------------------------------------------------------------
 * FormField — labeled input used by auth forms and admin forms.
 * For type="password", automatically renders an eye toggle so users can reveal
 * what they type. Toggling switches the actual input `type` between "password"
 * and "text". Non-password inputs render unchanged.
 * --------------------------------------------------------------------------- */
export function FormField({ label, testId, type = "text", required, value, onChange, placeholder, step, autoComplete, inputMode, children }) {
  const isPassword = type === "password";
  const [reveal, setReveal] = useState(false);
  const effectiveType = isPassword && reveal ? "text" : type;

  // Sensible mobile defaults — trigger the right on-screen keyboard and
  // enable browser autofill. Callers can still override via props.
  const guessedInputMode = inputMode
    || (type === "email" ? "email"
      : type === "tel"   ? "tel"
      : type === "number"? "decimal"
      : type === "url"   ? "url"
      : undefined);
  const guessedAutoComplete = autoComplete
    || (type === "password"                  ? "current-password"
      : type === "email"                     ? "email"
      : type === "tel"                       ? "tel"
      : type === "text" && /usuario/i.test(label || "") ? "username"
      : "off");

  return (
    <div>
      <label className="block text-[11px] font-mono uppercase tracking-wider text-[var(--ag7-text-muted)] mb-1.5">{label}</label>
      <div className="relative">
        {children != null ? (
          children
        ) : (
          <>
            <input
              type={effectiveType}
              required={required}
              step={step}
              autoComplete={guessedAutoComplete}
              inputMode={guessedInputMode}
              autoCapitalize={type === "email" || type === "password" ? "off" : undefined}
              autoCorrect={type === "password" ? "off" : undefined}
              spellCheck={type === "password" ? false : undefined}
              data-testid={testId}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
              className={`w-full bg-[var(--ag7-bg)] border border-zinc-800 rounded-xl px-4 py-3 text-white text-sm sm:text-xs font-mono placeholder:text-zinc-600 transition-colors duration-150 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500/40 disabled:opacity-50 disabled:cursor-not-allowed ${
                isPassword ? "pr-12" : ""
              }`}
            />
            {isPassword && (
              <button
                type="button"
                onClick={() => setReveal((r) => !r)}
                data-testid={testId ? `${testId}-reveal-btn` : undefined}
                aria-label={reveal ? "Ocultar contraseña" : "Mostrar contraseña"}
                title={reveal ? "Ocultar" : "Mostrar"}
                className="absolute inset-y-0 right-2 my-auto h-9 w-9 flex items-center justify-center rounded-lg text-zinc-400 hover:text-purple-300 hover:bg-[var(--ag7-surface-2)] transition-colors"
              >
                {reveal ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
